import { useCallback, useEffect, useRef } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import {
  closeCommandPalette,
  commandLogger,
  COMMANDS,
  openCommandPalette,
} from "./commands";
import { CommandPalette } from "./components/command-palette/command-palette";
import { useOS } from "./hooks/use-os";

import "./lib/sentry/instrument";
import "./index.css";

/* Query Client */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 6,
    },
  },
});

function App() {
  /* Refs */
  const dialogRef = useRef<HTMLDialogElement>(null);

  /* State */
  const OS = useOS();

  /* Post Message Event Listener */
  const postMessageListener = useCallback((event: MessageEvent) => {
    if (event.data === COMMANDS.OPEN_COMMAND_PALETTE) {
      commandLogger(event);
      dialogRef.current?.showModal();
      document.body.style.overflow = "hidden";
      const input = dialogRef.current?.querySelector("input");
      input?.focus();
    }

    if (event.data === COMMANDS.CLOSE_COMMAND_PALETTE) {
      commandLogger(event);
      dialogRef.current?.close();
      document.body.style.overflow = "auto";
    }
  }, []);

  /* Event Listeners */
  useEffect(() => {
    /** App listens for post-messages and determines whether to open page or not.
      - Docs: https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
    */

    window.addEventListener("message", postMessageListener);

    return () => {
      window.removeEventListener("message", postMessageListener);
    };
  }, [postMessageListener]);

  useEffect(() => {
    /* Create Hot-Key listeners for opening */
    const key = ["macos", "ios"].includes(OS) ? "metaKey" : "ctrlKey";

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event[key] && event.key === "k") {
        event.preventDefault();
        openCommandPalette();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [OS]);

  useEffect(() => {
    /* Close Command Palette on outside click */
    const handleOutsideClick = (event: MouseEvent) => {
      const dialog = dialogRef.current;
      if (event.target === dialog) {
        closeCommandPalette();
      }
    };

    const dialog = dialogRef.current;
    dialog?.addEventListener("click", handleOutsideClick);

    return () => {
      dialog?.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <CommandPalette ref={dialogRef} />
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}

export default App;
